import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  PaymentReturnBill,
  PaymentReturnInsertData,
  PaymentReturnVm,
  PaymentReturnListResponse,
  PaymentReturnListItem,
  ApiResponse,
} from '@/api/types'

/**
 * Payment Return management service
 * Handles all payment return-related API operations
 */
export class PaymentReturnService {
  /**
   * Get paginated list of payment returns for a branch
   */
  async getAllPaymentReturns(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<PaymentReturnListResponse> {
    const url = endpoints.paymentReturn.getAll(branchId, pageNumber, pageSize)
    console.log('🔵 [PaymentReturnService] getAllPaymentReturns URL:', url)
    const response = await apiClient.get<ApiResponse<PaymentReturnListResponse> | PaymentReturnListResponse | any>(url)
    console.log('🔵 [PaymentReturnService] getAllPaymentReturns Response:', response)
    const payload = response.data
    console.log('🔵 [PaymentReturnService] getAllPaymentReturns Payload:', payload)

    // Helper to map payment returns array
    const mapPaymentReturns = (items: any[]): PaymentReturnListItem[] => {
      return items.map(item => ({
        id: item.id ?? 0,
        billId: item.billId ?? 0,
        vendorId: item.vendorId ?? 0,
        recipientAccount: item.recipientAccount ?? 0,
        destinationAccount: item.destinationAccount ?? 0,
        amount: item.amount ?? 0,
        createdDate: item.createdDate ?? null,
        createdBy: item.createdBy ?? null,
        modifiedDate: item.modifiedDate ?? null,
        modifiedBy: item.modifiedBy ?? null,
        branchId: item.branchId ?? 0,
        returnNo: item.returnNo ?? null,
        date: item.date ?? null,
        note: item.note ?? null,
        returnType: item.returnType ?? null,
        billNo: item.billNo,
        vendorName: item.vendorName,
      }))
    }

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        const data = payload.data
        if (Array.isArray(data)) {
          return {
            totalRecords: payload.totalRecords ?? data.length,
            pageNumber: payload.pageNumber ?? pageNumber,
            pageSize: payload.pageSize ?? pageSize,
            data: mapPaymentReturns(data),
          }
        }
        return data as PaymentReturnListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: mapPaymentReturns(payload.data),
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: mapPaymentReturns(payload),
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ? mapPaymentReturns(payload.data) : [],
    }
  }

  /**
   * Get payment return by ID
   */
  async getPaymentReturnById(id: number, branchId: number): Promise<PaymentReturnListItem> {
    const url = endpoints.paymentReturn.getById(id, branchId)
    const response = await apiClient.get<ApiResponse<PaymentReturnListItem> | PaymentReturnListItem | any>(url)
    
    if (response.data && typeof response.data === 'object') {
      if ('data' in response.data) {
        return response.data.data as PaymentReturnListItem
      }
      return response.data as PaymentReturnListItem
    }
    
    throw new Error('Invalid response format from GetPaymentReturnById')
  }

  /**
   * Delete payment return
   */
  async deletePaymentReturn(id: number): Promise<any> {
    const url = endpoints.paymentReturn.delete(id)
    const response = await apiClient.delete<ApiResponse<any> | any>(url)
    return response.data
  }

  /**
   * Get bills by vendor ID
   */
  async getBillsByVendorId(vendorId: number): Promise<PaymentReturnBill[]> {
    const url = endpoints.paymentReturn.getBillsByVendorId(vendorId)
    const response = await apiClient.get<ApiResponse<PaymentReturnBill[]> | PaymentReturnBill[] | any>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    }
    if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    return []
  }

  /**
   * Get insert data for payment return
   */
  async getInsertData(branchId: number, billId: number): Promise<PaymentReturnInsertData> {
    const url = endpoints.paymentReturn.getInsertData(branchId, billId)
    const response = await apiClient.get<ApiResponse<PaymentReturnInsertData> | PaymentReturnInsertData | any>(url)
    
    if (response.data && typeof response.data === 'object') {
      if ('data' in response.data) {
        return response.data.data as PaymentReturnInsertData
      }
      return response.data as PaymentReturnInsertData
    }
    
    throw new Error('Invalid response format from GetInsertData')
  }

  /**
   * Save payment return (POST)
   */
  async savePaymentReturn(data: PaymentReturnVm): Promise<any> {
    const url = endpoints.paymentReturn.save
    console.log('🔵 [PaymentReturnService] POST Request URL:', url)
    console.log('🔵 [PaymentReturnService] POST Request Data:', JSON.stringify(data, null, 2))
    console.log('🔵 [PaymentReturnService] POST Request Data Object:', data)
    
    try {
      const response = await apiClient.post<ApiResponse<any> | any>(url, data)
      console.log('✅ [PaymentReturnService] POST Response:', response)
      return response.data
    } catch (error: any) {
      console.error('❌ [PaymentReturnService] POST Error:', error)
      console.error('❌ [PaymentReturnService] POST Error Response:', error.response)
      console.error('❌ [PaymentReturnService] POST Error Data:', error.response?.data)
      throw error
    }
  }

  /**
   * Update payment return (PUT)
   */
  async updatePaymentReturn(data: PaymentReturnVm): Promise<any> {
    const url = endpoints.paymentReturn.update
    const response = await apiClient.put<ApiResponse<any> | any>(url, data)
    return response.data
  }
}

export const paymentReturnService = new PaymentReturnService()
